import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPlus, FiActivity, FiUsers } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

const PayrollCreate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { month, year, staffId } = useParams();
  
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [earnings, setEarnings] = useState([{ type: '', amount: '' }]);
  const [deductions, setDeductions] = useState([{ type: '', amount: '' }]);
  
  const [payrollSummary, setPayrollSummary] = useState({
    basic: 0,
    total_allowance: 0,
    total_deduction: 0,
    gross_salary: 0,
    tax: 0,
    net_salary: 0
  });

  useEffect(() => {
    fetchStaffDetails();
  }, [staffId]);

  const fetchStaffDetails = async () => {
    try {
      const response = await payrollAPI.getStaffDetails(staffId);
      if (response.data.success) {
        const staffData = response.data.data;
        setStaff(staffData);
        setPayrollSummary(prev => ({
          ...prev,
          basic: parseFloat(staffData.basic_salary || 0)
        }));
      }
    } catch (error) {
      toast.error('Error fetching staff details');
      console.error(error);
      navigate('/hrms/payroll');
    } finally {
      setLoading(false);
    }
  };

  const addEarning = () => {
    setEarnings([...earnings, { type: '', amount: '' }]);
  };

  const removeEarning = (index) => {
    if (earnings.length > 1) {
      setEarnings(earnings.filter((_, i) => i !== index));
    }
  };

  const updateEarning = (index, field, value) => {
    const newEarnings = [...earnings];
    newEarnings[index][field] = value;
    setEarnings(newEarnings);
  };

  const addDeduction = () => {
    setDeductions([...deductions, { type: '', amount: '' }]);
  };

  const removeDeduction = (index) => {
    if (deductions.length > 1) {
      setDeductions(deductions.filter((_, i) => i !== index));
    }
  };

  const updateDeduction = (index, field, value) => {
    const newDeductions = [...deductions];
    newDeductions[index][field] = value;
    setDeductions(newDeductions);
  };

  const calculatePayroll = () => {
    const totalAllowance = earnings.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const totalDeduction = deductions.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const grossSalary = parseFloat(payrollSummary.basic) + totalAllowance - totalDeduction;
    const netSalary = grossSalary - parseFloat(payrollSummary.tax || 0);

    setPayrollSummary({
      ...payrollSummary,
      total_allowance: totalAllowance,
      total_deduction: totalDeduction,
      gross_salary: grossSalary,
      net_salary: netSalary
    });

    toast.success('Payroll calculated successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (payrollSummary.net_salary <= 0) {
      toast.error('Please calculate payroll before saving');
      return;
    }

    setSubmitting(true);
    try {
      // Filter out empty earnings and deductions
      const validEarnings = earnings.filter(e => e.type && parseFloat(e.amount || 0) > 0);
      const validDeductions = deductions.filter(d => d.type && parseFloat(d.amount || 0) > 0);
      
      const response = await payrollAPI.savePayslip({
        staff_id: parseInt(staffId),
        month: month,
        year: parseInt(year),
        basic: payrollSummary.basic,
        allowance_type: validEarnings.map(e => e.type),
        allowance_amount: validEarnings.map(e => parseFloat(e.amount)),
        total_allowance: payrollSummary.total_allowance,
        deduction_type: validDeductions.map(d => d.type),
        deduction_amount: validDeductions.map(d => parseFloat(d.amount)),
        total_deduction: payrollSummary.total_deduction,
        gross_salary: payrollSummary.gross_salary,
        tax: payrollSummary.tax,
        net_salary: payrollSummary.net_salary
      });

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/hrms/payroll');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving payslip');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="mt-4 text-gray-600">Loading staff details...</p>
      </div>
    );
  }

  if (!staff) {
    return null;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            <FiUsers className="inline mr-2" /> Human Resource
          </h1>
          <button
            onClick={() => navigate('/hrms/payroll')}
            className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md"
          >
            <FiArrowLeft className="inline" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Staff Details</h3>
        </div>

        {/* Staff Details Section */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={staff.image || '/default-avatar.png'}
                      alt={staff.name}
                      className="w-28 h-28 rounded-lg object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b">
                          <th className="py-2 text-left font-semibold w-1/4">Name</th>
                          <td className="py-2">{staff.name} {staff.surname}</td>
                          <th className="py-2 text-left font-semibold w-1/4">Staff ID</th>
                          <td className="py-2">{staff.employee_id}</td>
                        </tr>
                        <tr className="border-b">
                          <th className="py-2 text-left font-semibold">Phone</th>
                          <td className="py-2">{staff.contact_no}</td>
                          <th className="py-2 text-left font-semibold">Email</th>
                          <td className="py-2">{staff.email}</td>
                        </tr>
                        <tr className="border-b">
                          <th className="py-2 text-left font-semibold">EPF No</th>
                          <td className="py-2">{staff.epf_no || '-'}</td>
                          <th className="py-2 text-left font-semibold">Role</th>
                          <td className="py-2">{staff.role_name}</td>
                        </tr>
                        <tr>
                          <th className="py-2 text-left font-semibold">Department</th>
                          <td className="py-2">{staff.department_name || '-'}</td>
                          <th className="py-2 text-left font-semibold">Designation</th>
                          <td className="py-2">{staff.designation_name || '-'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="mb-2">
                  <div className="bg-primary-600 text-white text-center py-2 rounded-t-md font-semibold">
                    Attendance
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 text-left">Month</th>
                      <th className="py-1 text-center" title="Present">P</th>
                      <th className="py-1 text-center" title="Late">L</th>
                      <th className="py-1 text-center" title="Absent">A</th>
                      <th className="py-1 text-center" title="Half Day">F</th>
                      <th className="py-1 text-center" title="Holiday">H</th>
                      <th className="py-1 text-center" title="Approved Leave">V</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b text-center">
                      <td className="py-2 text-left">October</td>
                      <td>0</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td>
                    </tr>
                    <tr className="border-b text-center">
                      <td className="py-2 text-left">September</td>
                      <td>0</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td>
                    </tr>
                    <tr className="text-center">
                      <td className="py-2 text-left">August</td>
                      <td>0</td><td>0</td><td>0</td><td>0</td><td>0</td><td>0</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-300 my-6"></div>

          {/* Payroll Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Earning Column */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Earning</h3>
                  <button
                    type="button"
                    onClick={addEarning}
                    className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-full"
                    title="Add more"
                  >
                    <FiPlus />
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  {earnings.map((earning, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Type"
                        value={earning.type}
                        onChange={(e) => updateEarning(index, 'type', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        value={earning.amount}
                        onChange={(e) => updateEarning(index, 'amount', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                        min="0"
                        step="0.01"
                      />
                      {earnings.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEarning(index)}
                          className="px-2 text-red-600 hover:text-red-700"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Deduction Column */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Deduction</h3>
                  <button
                    type="button"
                    onClick={addDeduction}
                    className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-full"
                    title="Add more"
                  >
                    <FiPlus />
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  {deductions.map((deduction, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Type"
                        value={deduction.type}
                        onChange={(e) => updateDeduction(index, 'type', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      />
                      <input
                        type="number"
                        placeholder="Amount"
                        value={deduction.amount}
                        onChange={(e) => updateDeduction(index, 'amount', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                        min="0"
                        step="0.01"
                      />
                      {deductions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDeduction(index)}
                          className="px-2 text-red-600 hover:text-red-700"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payroll Summary Column */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Payroll Summary (₹)</h3>
                  <button
                    type="button"
                    onClick={calculatePayroll}
                    className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-full"
                    title="Calculate"
                  >
                    <FiActivity />
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Basic Salary
                    </label>
                    <input
                      type="any"
                      value={payrollSummary.basic}
                      onChange={(e) => setPayrollSummary({ ...payrollSummary, basic: parseFloat(e.target.value || 0) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      step="0.01"
                      
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Earning
                    </label>
                    <input
                      type="text"
                      value={payrollSummary.total_allowance.toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deduction
                    </label>
                    <input
                      type="text"
                      value={payrollSummary.total_deduction.toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-red-600"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gross Salary
                    </label>
                    <input
                      type="text"
                      value={payrollSummary.gross_salary.toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax
                    </label>
                    <input
                      type="any"
                      value={payrollSummary.tax}
                      onChange={(e) => {
                        const tax = parseFloat(e.target.value || 0);
                        setPayrollSummary({
                          ...payrollSummary,
                          tax: tax,
                          net_salary: payrollSummary.gross_salary - tax
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none text-red-600"
                      step="0.01"
                     
                    />
                  </div>
                  
                  <div className="border-t border-gray-300 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Net Salary
                    </label>
                    <input
                      type="text"
                      value={payrollSummary.net_salary.toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-green-50 text-green-700 font-bold text-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PayrollCreate;
